import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Receipt, Users, ArrowRight, Check, Trash2, Calculator } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const CATEGORIES = [
  { value: 'food', label: '🍕 Food & Drinks', color: 'category-food' },
  { value: 'transport', label: '🚗 Transport', color: 'category-transport' },
  { value: 'shopping', label: '🛍️ Shopping', color: 'category-shopping' },
  { value: 'entertainment', label: '🎮 Entertainment', color: 'category-entertainment' },
  { value: 'utilities', label: '💡 Utilities', color: 'category-utilities' },
  { value: 'rent', label: '🏠 Rent', color: 'category-rent' },
  { value: 'general', label: '📦 General', color: 'category-general' }
];

const Expenses = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [balances, setBalances] = useState({ debts: [] });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showSettleUp, setShowSettleUp] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    total_amount: '',
    paid_by: user.id,
    category: 'general',
    split_type: 'equal',
    notes: ''
  });

  // Custom splits state - tracks amounts/percentages per member
  const [customSplits, setCustomSplits] = useState({});
  const [selectedMembers, setSelectedMembers] = useState({});

  useEffect(() => {
    fetchData();
  }, [groupId]);

  // Initialize custom splits when members are loaded or split type changes
  useEffect(() => {
    if (members.length > 0) {
      const initialSplits = {};
      const initialSelected = {};
      members.forEach(m => {
        initialSplits[m.id] = expenseForm.split_type === 'percentage' ? '' : '';
        initialSelected[m.id] = true; // All members selected by default
      });
      setCustomSplits(initialSplits);
      setSelectedMembers(initialSelected);
    }
  }, [members, expenseForm.split_type]);

  const fetchData = async () => {
    try {
      const [groupRes, expensesRes, balancesRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/expenses/group/${groupId}`),
        axios.get(`${API}/groups/${groupId}/balances`)
      ]);
      
      setGroup(groupRes.data);
      setExpenses(expensesRes.data);
      setBalances(balancesRes.data);

      // Fetch member details
      const memberPromises = groupRes.data.member_ids.map(id => 
        axios.get(`${API}/users/${id}`).catch(() => ({ data: { id, name: 'Unknown' } }))
      );
      const membersData = await Promise.all(memberPromises);
      setMembers(membersData.map(res => res.data));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate remaining amount for custom splits
  const calculateRemaining = () => {
    const total = parseFloat(expenseForm.total_amount) || 0;
    const allocated = Object.entries(customSplits)
      .filter(([id]) => selectedMembers[id])
      .reduce((sum, [, amount]) => sum + (parseFloat(amount) || 0), 0);
    return total - allocated;
  };

  // Calculate remaining percentage
  const calculateRemainingPercentage = () => {
    const allocated = Object.entries(customSplits)
      .filter(([id]) => selectedMembers[id])
      .reduce((sum, [, pct]) => sum + (parseFloat(pct) || 0), 0);
    return 100 - allocated;
  };

  // Split equally among selected members
  const splitEqually = () => {
    const total = parseFloat(expenseForm.total_amount) || 0;
    const selectedCount = Object.values(selectedMembers).filter(Boolean).length;
    if (selectedCount === 0) return;
    
    const perPerson = (total / selectedCount).toFixed(2);
    const newSplits = {};
    members.forEach(m => {
      newSplits[m.id] = selectedMembers[m.id] ? perPerson : '';
    });
    setCustomSplits(newSplits);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.description || !expenseForm.total_amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Prepare splits based on split type
    let splits = null;
    const total = parseFloat(expenseForm.total_amount);
    
    if (expenseForm.split_type === 'equal') {
      // For equal split, create splits for all selected members
      const selectedMembersList = members.filter(m => selectedMembers[m.id]);
      if (selectedMembersList.length === 0) {
        toast.error('Please select at least one member to split with');
        return;
      }
      const perPerson = (total / selectedMembersList.length).toFixed(2);
      splits = selectedMembersList.map(m => ({
        user_id: m.id,
        amount: parseFloat(perPerson),
        paid: m.id === expenseForm.paid_by
      }));
    } else if (expenseForm.split_type === 'custom') {
      // Validate custom splits
      const remaining = calculateRemaining();
      if (Math.abs(remaining) > 0.01) {
        toast.error(`Please allocate the remaining ₹${remaining.toFixed(2)}`);
        return;
      }
      
      splits = members
        .filter(m => selectedMembers[m.id] && parseFloat(customSplits[m.id]) > 0)
        .map(m => ({
          user_id: m.id,
          amount: parseFloat(customSplits[m.id]),
          paid: m.id === expenseForm.paid_by
        }));
    } else if (expenseForm.split_type === 'percentage') {
      const remainingPct = calculateRemainingPercentage();
      if (Math.abs(remainingPct) > 0.01) {
        toast.error(`Percentages must add up to 100% (${remainingPct.toFixed(1)}% remaining)`);
        return;
      }
      
      splits = members
        .filter(m => selectedMembers[m.id] && parseFloat(customSplits[m.id]) > 0)
        .map(m => ({
          user_id: m.id,
          amount: parseFloat((total * parseFloat(customSplits[m.id]) / 100).toFixed(2)),
          percentage: parseFloat(customSplits[m.id]),
          paid: m.id === expenseForm.paid_by
        }));
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/expenses?user_id=${user.id}`, {
        group_id: groupId,
        description: expenseForm.description,
        total_amount: parseFloat(expenseForm.total_amount),
        paid_by: expenseForm.paid_by,
        category: expenseForm.category,
        split_type: expenseForm.split_type === 'custom' ? 'exact' : expenseForm.split_type,
        splits: splits,
        notes: expenseForm.notes
      });
      
      toast.success('Expense added! 🎉');
      setShowAddExpense(false);
      setExpenseForm({
        description: '',
        total_amount: '',
        paid_by: user.id,
        category: 'general',
        split_type: 'equal',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSettleUp = async () => {
    if (!selectedDebt) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/settlements?group_id=${groupId}&from_user=${selectedDebt.from_user_id}&to_user=${selectedDebt.to_user_id}&amount=${selectedDebt.amount}`
      );
      
      toast.success('Payment recorded! 💰');
      setShowSettleUp(false);
      setSelectedDebt(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to record settlement');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}?user_id=${user.id}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const getMemberName = (id) => members.find(m => m.id === id)?.name || 'Unknown';
  const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[6];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate(`/group/${groupId}`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button onClick={() => setShowAddExpense(true)} className="rounded-full gradient-primary" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-2" data-testid="expenses-title">Expenses & Splits</h1>
          <p className="text-muted-foreground mb-8">Track and split expenses in {group?.name}</p>
        </motion.div>

        <Tabs defaultValue="balances" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="balances" data-testid="balances-tab">Who Owes Whom</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="expenses-tab">All Expenses</TabsTrigger>
          </TabsList>

          <TabsContent value="balances">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-card rounded-2xl p-6 border border-border mb-6"
            >
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-violet-600" />
                <h2 className="text-lg font-semibold">Outstanding Balances</h2>
              </div>

              {balances.debts.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-lg font-semibold text-green-600">All settled up! 🎉</p>
                  <p className="text-muted-foreground">No outstanding balances</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {balances.debts.map((debt, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-xl bg-muted/50 border border-border gap-3"
                    >
                      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                            <span className="text-red-700 dark:text-red-300 font-semibold text-xs sm:text-sm">
                              {debt.from_user_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm sm:text-base">{debt.from_user_name}</p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">owes</p>
                          </div>
                        </div>

                        <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />

                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <span className="text-green-700 dark:text-green-300 font-semibold text-xs sm:text-sm">
                              {debt.to_user_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm sm:text-base">{debt.to_user_name}</p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">receives</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                        <p className="text-lg sm:text-xl font-bold gradient-text">₹{debt.amount}</p>
                        {(debt.from_user_id === user.id || debt.to_user_id === user.id) && (
                          <Button
                            onClick={() => { setSelectedDebt(debt); setShowSettleUp(true); }}
                            size="sm"
                            className="rounded-full text-xs sm:text-sm px-3 sm:px-4"
                            variant={debt.from_user_id === user.id ? "default" : "outline"}
                          >
                            {debt.from_user_id === user.id ? 'Pay' : 'Remind'}
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          <TabsContent value="expenses">
            <div className="space-y-4">
              {expenses.length === 0 ? (
                <div className="bg-card rounded-2xl p-12 text-center border border-border">
                  <Receipt className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No expenses yet</h3>
                  <p className="text-muted-foreground mb-6">Add your first expense to start splitting</p>
                  <Button onClick={() => setShowAddExpense(true)} className="rounded-full gradient-primary">
                    <Plus className="w-4 h-4 mr-2" /> Add First Expense
                  </Button>
                </div>
              ) : (
                expenses.map((expense, index) => {
                  const catInfo = getCategoryInfo(expense.category);
                  const paidByUser = getMemberName(expense.paid_by);
                  const userSplit = expense.splits?.find(s => s.user_id === user.id);
                  
                  return (
                    <motion.div
                      key={expense.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-card rounded-2xl p-5 border border-border card-hover"
                      data-testid={`expense-${index}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                          <div className={`w-12 h-12 rounded-xl ${catInfo.color} flex items-center justify-center text-xl`}>
                            {catInfo.label.split(' ')[0]}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{expense.description}</h3>
                            <p className="text-sm text-muted-foreground">
                              Paid by <span className="font-medium">{paidByUser}</span> • {new Date(expense.date || expense.created_at).toLocaleDateString()}
                            </p>
                            <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${catInfo.color}`}>
                              {catInfo.label}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">₹{expense.total_amount}</p>
                          {userSplit && !userSplit.paid && expense.paid_by !== user.id && (
                            <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                              You owe ₹{userSplit.amount}
                            </p>
                          )}
                          {expense.paid_by === user.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteExpense(expense.id)}
                              className="mt-2 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {expense.splits && expense.splits.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground mb-2">Split between:</p>
                          <div className="flex flex-wrap gap-2">
                            {expense.splits.map((split, i) => (
                              <span
                                key={i}
                                className={`text-xs px-2 py-1 rounded-full ${
                                  split.paid ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {getMemberName(split.user_id)}: ₹{split.amount}
                                {split.paid && ' ✓'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Expense Dialog */}
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>Split an expense with your group members</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddExpense} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="What was this for?"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                className="rounded-xl mt-2"
                data-testid="expense-description"
              />
            </div>
            
            <div>
              <Label htmlFor="amount">Total Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="0.00"
                value={expenseForm.total_amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, total_amount: e.target.value })}
                className="rounded-xl mt-2 text-2xl font-bold"
                data-testid="expense-amount"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Paid by</Label>
                <Select
                  value={expenseForm.paid_by}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, paid_by: value })}
                >
                  <SelectTrigger className="rounded-xl mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name} {member.id === user.id && '(You)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Category</Label>
                <Select
                  value={expenseForm.category}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}
                >
                  <SelectTrigger className="rounded-xl mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Split Type</Label>
              <Select
                value={expenseForm.split_type}
                onValueChange={(value) => setExpenseForm({ ...expenseForm, split_type: value })}
              >
                <SelectTrigger className="rounded-xl mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equal">Split Equally</SelectItem>
                  <SelectItem value="custom">Custom Amounts (per person)</SelectItem>
                  <SelectItem value="percentage">By Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Equal Split Member Selection */}
            {expenseForm.split_type === 'equal' && (
              <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-semibold">Select members to split with</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const allSelected = {};
                      members.forEach(m => { allSelected[m.id] = true; });
                      setSelectedMembers(allSelected);
                    }}
                    className="rounded-full text-xs"
                  >
                    Select All
                  </Button>
                </div>

                {members.map(member => {
                  const selectedCount = Object.values(selectedMembers).filter(Boolean).length;
                  const perPerson = selectedCount > 0 ? (parseFloat(expenseForm.total_amount || 0) / selectedCount).toFixed(2) : '0.00';
                  
                  return (
                    <div key={member.id} className="flex items-center justify-between gap-3 py-1">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={`eq-member-${member.id}`}
                          checked={selectedMembers[member.id]}
                          onCheckedChange={(checked) => {
                            setSelectedMembers({ ...selectedMembers, [member.id]: checked });
                          }}
                        />
                        <Label htmlFor={`eq-member-${member.id}`} className="text-sm cursor-pointer">
                          {member.name} {member.id === user.id && '(You)'}
                        </Label>
                      </div>
                      {selectedMembers[member.id] && (
                        <span className="text-sm text-muted-foreground">₹{perPerson}</span>
                      )}
                    </div>
                  );
                })}
                
                {/* Summary */}
                {expenseForm.total_amount && Object.values(selectedMembers).filter(Boolean).length > 0 && (
                  <div className="text-sm text-muted-foreground pt-2 border-t border-border">
                    Each person pays: <span className="font-semibold text-foreground">
                      ₹{(parseFloat(expenseForm.total_amount) / Object.values(selectedMembers).filter(Boolean).length).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Custom Split Section */}
            {(expenseForm.split_type === 'custom' || expenseForm.split_type === 'percentage') && (
              <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-semibold">
                    {expenseForm.split_type === 'custom' ? 'Enter amount for each person' : 'Enter percentage for each person'}
                  </Label>
                  {expenseForm.split_type === 'custom' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={splitEqually}
                      className="rounded-full text-xs"
                    >
                      <Calculator className="w-3 h-3 mr-1" /> Split Equally
                    </Button>
                  )}
                </div>

                {members.map(member => (
                  <div key={member.id} className="flex items-center gap-3">
                    <Checkbox
                      id={`member-${member.id}`}
                      checked={selectedMembers[member.id]}
                      onCheckedChange={(checked) => {
                        setSelectedMembers({ ...selectedMembers, [member.id]: checked });
                        if (!checked) {
                          setCustomSplits({ ...customSplits, [member.id]: '' });
                        }
                      }}
                    />
                    <Label htmlFor={`member-${member.id}`} className="flex-1 text-sm">
                      {member.name} {member.id === user.id && '(You)'}
                    </Label>
                    <div className="relative w-28">
                      <Input
                        type="number"
                        placeholder="0"
                        value={customSplits[member.id] || ''}
                        onChange={(e) => setCustomSplits({ ...customSplits, [member.id]: e.target.value })}
                        disabled={!selectedMembers[member.id]}
                        className="rounded-lg text-right pr-8"
                        data-testid={`split-${member.id}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        {expenseForm.split_type === 'percentage' ? '%' : '₹'}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Remaining indicator */}
                <div className={`text-sm font-medium text-right pt-2 border-t border-border ${
                  expenseForm.split_type === 'custom' 
                    ? (Math.abs(calculateRemaining()) < 0.01 ? 'text-green-600' : 'text-orange-600')
                    : (Math.abs(calculateRemainingPercentage()) < 0.01 ? 'text-green-600' : 'text-orange-600')
                }`}>
                  {expenseForm.split_type === 'custom' ? (
                    <>Remaining: ₹{calculateRemaining().toFixed(2)}</>
                  ) : (
                    <>Remaining: {calculateRemainingPercentage().toFixed(1)}%</>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any details..."
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                className="rounded-xl mt-2"
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-full gradient-primary"
              disabled={submitting}
              data-testid="submit-expense-btn"
            >
              {submitting ? 'Adding...' : 'Add Expense'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settle Up Dialog */}
      <Dialog open={showSettleUp} onOpenChange={setShowSettleUp}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Settle Up</DialogTitle>
            <DialogDescription>Record a payment</DialogDescription>
          </DialogHeader>
          {selectedDebt && (
            <div className="py-6 text-center">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-bold">{selectedDebt.from_user_name[0]}</span>
                  </div>
                  <p className="text-sm font-medium">{selectedDebt.from_user_name}</p>
                </div>
                <ArrowRight className="w-6 h-6 text-muted-foreground" />
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-bold">{selectedDebt.to_user_name[0]}</span>
                  </div>
                  <p className="text-sm font-medium">{selectedDebt.to_user_name}</p>
                </div>
              </div>
              <p className="text-4xl font-bold gradient-text mb-6">₹{selectedDebt.amount}</p>
              <Button
                onClick={handleSettleUp}
                className="w-full rounded-full gradient-primary"
                disabled={submitting}
              >
                {submitting ? 'Recording...' : 'Record Payment'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
